/* =========================================================================
 * Kkumi Reviews Marquee ("In your words") — the carousel below the header.
 *
 *  - Reviews come from the Judge.me proxy plus the section's manual blocks
 *    (emitted as JSON in [data-rm-manual]). Each is normalised to one shape
 *    and sorted by what it holds:
 *      photo (or video poster) -> full-height photo tile
 *      text, no photo          -> packed into columns of 1-3 cards
 *      neither                 -> a pill in the reverse "bubbles" strip
 *  - Text columns are packed by MEASURING each card at the current card
 *    width, so quotes are never clamped; a review too long for even a
 *    full-height card is left out (it is still on the full reviews page).
 *  - Below 1000px the main lane is a native swipe-and-snap row with no
 *    duplicates. At 1000px+ (motion allowed) it becomes a marquee: the set
 *    is duplicated once (aria-hidden, untabbable) for a seamless loop.
 *  - Tapping a card opens the full review in the modal (bottom sheet on
 *    mobile), with focus trapped and returned to the card on close.
 *  - The header's live stats (applyStats) are unchanged.
 * ========================================================================= */
(function () {
  'use strict';

  var CACHE_TTL_MS = 30 * 60 * 1000; // 30 min

  function log() {}
  function warn() {}
  function err() {}

  // Brand heart SVG path — keep in sync with snippets/kkumi-hearts.liquid
  // so all "review hearts" across the site render identically.
  var HEART_PATH = 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';
  function heartSvg(state, size, pct) {
    var s = size || 13;
    if (state === 'partial') {
      // Fill in proportion to the remainder — a 4.8 average paints four solid
      // hearts and one 80% across, never five solid (which reads as 5.0).
      var inset = 100 - Math.max(0, Math.min(100, pct));
      return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="' + HEART_PATH + '" fill="currentColor" style="clip-path: inset(0 ' + inset + '% 0 0);"/>' +
        '<path d="' + HEART_PATH + '" fill="none"/></svg>';
    }
    var fill = state === 'empty' ? 'none' : 'currentColor';
    return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="' + fill + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + HEART_PATH + '"/></svg>';
  }
  function heartsHtml(rating, size) {
    var v = Math.max(0, Math.min(5, Number(rating) || 0));
    var full = Math.floor(v);
    var pct = Math.round((v - full) * 100);
    // Snap the extremes so a rounding crumb never renders a sliver of fill or gap.
    var hasPartial = pct >= 3 && pct < 98;
    if (pct >= 98) full += 1;
    var out = '';
    for (var i = 0; i < 5; i++) {
      if (i < full) out += heartSvg('full', size);
      else if (i === full && hasPartial) out += heartSvg('partial', size, pct);
      else out += heartSvg('empty', size);
    }
    return out;
  }

  // Card hearts are whole ratings in two flat colours (filled / empty), per
  // the carousel design; the outlined partial hearts above stay for the
  // header's live average.
  function cardHearts(rating) {
    var v = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"' + (i > v ? ' class="is-off"' : '') + '><path d="' + HEART_PATH + '"/></svg>';
    }
    return out;
  }

  // Judge.me picture shape varies. `prefer` picks the size for the context:
  // cards want a mid-size rendition, the modal the original.
  function pictureUrl(pic, prefer) {
    if (!pic) return null;
    if (typeof pic === 'string') return pic;
    var u = pic.urls || pic;
    if (prefer === 'card') return u.huge || u.large || u.original || u.medium || u.url || u.src || null;
    return u.original || u.huge || u.large || u.medium || u.url || u.href || u.src || u.image || null;
  }
  function safeImageUrl(url) {
    if (typeof url !== 'string') return null;
    if (/^\/\//.test(url)) url = 'https:' + url; // Shopify CDN URLs are protocol-relative
    return /^https:\/\//i.test(url) ? url : null;
  }
  // Product links: same-site paths or https only.
  function safeLink(url) {
    if (typeof url !== 'string' || !url) return '';
    if (/^\/(?!\/)/.test(url)) return url;
    return /^https:\/\//i.test(url) ? url : '';
  }

  // Copy strings read off the section root's data-* (set per-render); the
  // literals are the fallback when no setting is emitted.
  var COPY = {
    reviewerFallback: 'Anonymous',
    verifiedLabel: 'Verified',
    readLabel: 'Read review',
    watchLabel: 'Watch review',
    byline: 'by [name]',
    cardAria: "Read [name]'s review of [product]",
    photoAlt: "[name]'s deco"
  };

  function reviewerName(r) {
    if (r.reviewer && r.reviewer.name) return r.reviewer.name;
    return r.reviewer_name || r.name || COPY.reviewerFallback;
  }
  function productLabel(r) {
    return r.product_title || r.product_handle || '';
  }

  // Imported reviews often required a body, so the importer filled it with the
  // product title. That reads as noise next to the product pill, so treat a
  // body/title that is just the product name as if the review had no text at
  // all (the card then shows the rating on its own).
  function normalizeText(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }
  function reviewText(r) {
    var text = (r.body || r.title || '').trim();
    if (!text) return '';
    var product = normalizeText(productLabel(r));
    if (product && normalizeText(text) === product) return '';
    return text;
  }

  var el = window.KKEl; // shared element builder (assets/kkumi-el.js)

  // ---- One review shape ---------------------------------------------------
  // { name, rating, verified, product, url, body, img, imgFull, poster, video[] }
  function fromApi(r) {
    var pic = (r.pictures || [])[0];
    var img = safeImageUrl(pictureUrl(pic, 'card'));
    if (pic && !img) warn('picture present but URL not resolved', pic);
    return {
      name: reviewerName(r),
      rating: Number(r.rating) || 0,
      verified: !!r.verified_buyer,
      product: productLabel(r),
      url: r.product_handle ? '/products/' + encodeURIComponent(r.product_handle) : '',
      body: reviewText(r),
      img: img,
      imgFull: safeImageUrl(pictureUrl(pic)) || img,
      video: []
    };
  }

  function fromManual(m) {
    var video = (m.video || []).filter(function (v) { return v && safeImageUrl(v.url); })
      .map(function (v) { return { url: safeImageUrl(v.url), type: String(v.type || '') }; });
    var img = safeImageUrl(m.img) || safeImageUrl(m.poster);
    return {
      name: (m.name || '').trim() || COPY.reviewerFallback,
      rating: Number(m.rating) || 0,
      verified: !!m.verified,
      product: (m.product || '').trim(),
      url: safeLink(m.url),
      body: (m.body || '').trim(),
      img: img,
      imgFull: img,
      video: video
    };
  }

  function kindOf(item) {
    if (item.img || item.video.length) return 'photo';
    if (item.body) return 'text';
    return 'bubble';
  }

  // Quote length class drives the type treatment.
  function lenClass(body) {
    var n = body.length;
    return n < 50 ? 'short' : n < 120 ? 'med' : 'long';
  }

  function fill(tpl, item) {
    var out = String(tpl || '');
    if (!item.product) out = out.replace(/\s*\S*\s*\[product\]/g, '');
    return out.replace(/\[name\]/g, item.name).replace(/\[product\]/g, item.product).trim();
  }

  // ---- Card builders ------------------------------------------------------
  function heartsEl(rating) {
    var h = el('span', 'kk-rm__hearts');
    h.setAttribute('role', 'img');
    h.setAttribute('aria-label', Math.round(rating) + ' out of 5');
    h.innerHTML = cardHearts(rating);
    return h;
  }

  function verifiedEl(item, ui) {
    if (!item.verified || !ui.showVerified) return null;
    var v = el('span', 'kk-rm__ok');
    if (window.KKIcons) v.innerHTML = window.KKIcons.html('check');
    v.appendChild(document.createTextNode(COPY.verifiedLabel));
    return v;
  }

  function whoEl(item) {
    var who = el('span', 'kk-rm__who');
    if (item.product) who.appendChild(el('span', 'kk-rm__prod', item.product));
    who.appendChild(el('span', 'kk-rm__name', fill(COPY.byline, item)));
    return who;
  }

  function cardShell(cls, idx, item) {
    var b = el('button', 'kk-rm__rc ' + cls);
    b.type = 'button';
    b.setAttribute('data-rm-i', idx);
    b.setAttribute('aria-haspopup', 'dialog');
    b.setAttribute('aria-label', fill(COPY.cardAria, item));
    return b;
  }

  function buildPhoto(item, idx, ui) {
    var card = cardShell('kk-rm__rc--photo', idx, item);
    if (item.img) {
      var img = el('img', 'kk-rm__rc-img kk-img-skel');
      img.src = item.img;
      img.alt = fill(COPY.photoAlt, item);
      img.loading = 'lazy';
      img.decoding = 'async';
      card.appendChild(img);
    }
    var top = el('span', 'kk-rm__rc-top');
    top.appendChild(heartsEl(item.rating));
    var ok = verifiedEl(item, ui);
    if (ok) top.appendChild(ok);
    card.appendChild(top);
    var plate = el('span', 'kk-rm__plate');
    plate.appendChild(whoEl(item));
    if (ui.showCta) plate.appendChild(el('span', 'kk-rm__cta', item.video.length ? COPY.watchLabel : COPY.readLabel));
    card.appendChild(plate);
    return card;
  }

  function buildText(item, idx, ui, full, grow) {
    var cls = 'kk-rm__rc--text kk-rm__rc--' + lenClass(item.body) + (full ? ' kk-rm__rc--full' : '') + (grow ? ' is-grow' : '');
    var card = cardShell(cls, idx, item);
    var row = el('span', 'kk-rm__rc-row');
    row.appendChild(heartsEl(item.rating));
    var ok = verifiedEl(item, ui);
    if (ok) row.appendChild(ok);
    card.appendChild(row);
    var copy = el('span', 'kk-rm__copy');
    if (full) {
      var mark = el('span', 'kk-rm__mark', '“');
      mark.setAttribute('aria-hidden', 'true');
      copy.appendChild(mark);
    }
    copy.appendChild(el('span', 'kk-rm__q', item.body));
    card.appendChild(copy);
    card.appendChild(whoEl(item));
    return card;
  }

  function buildBubble(item) {
    var b = el('span', 'kk-rm__bub');
    b.appendChild(heartsEl(item.rating));
    if (item.product) b.appendChild(el('span', 'kk-rm__bub-prod', item.product));
    b.appendChild(el('span', 'kk-rm__bub-who', item.name));
    return b;
  }

  // ---- Column packing -----------------------------------------------------
  // Greedy, in order: a column closes at 3 cards or when the next card would
  // overflow the card height. In a 2-3 card column the tallest card grows to
  // absorb the slack (all grow when they share a length class); a lone card
  // becomes a full card with the quote mark.
  function pack(wrap, items, textIdx, ui) {
    var probe = el('div', 'kk-rm__probe');
    probe.setAttribute('aria-hidden', 'true');
    wrap.appendChild(probe);
    var cs = getComputedStyle(wrap);
    var CH = parseFloat(cs.getPropertyValue('--kk-rm-ch')) || 400;
    var G = parseFloat(cs.getPropertyValue('--kk-rm-gap')) || 16;

    function measure(idx, full) {
      probe.textContent = '';
      var node;
      if (full) {
        node = el('div', 'kk-rm__stack');
        node.style.height = 'auto';
        node.appendChild(buildText(items[idx], idx, ui, true, false));
      } else {
        node = buildText(items[idx], idx, ui, false, false);
      }
      probe.appendChild(node);
      return node.getBoundingClientRect().height;
    }

    var cols = [];
    var cur = [];
    var sum = 0;
    textIdx.forEach(function (idx) {
      if (measure(idx, true) > CH) return; // too long even as a full card
      var h = measure(idx, false);
      if (cur.length && (cur.length === 3 || sum + G + h > CH)) { cols.push(cur); cur = []; sum = 0; }
      cur.push({ i: idx, h: h });
      sum += (cur.length > 1 ? G : 0) + h;
    });
    if (cur.length) cols.push(cur);
    probe.remove();

    return cols.map(function (c) {
      var stack = el('div', 'kk-rm__stack');
      if (c.length === 1) {
        stack.appendChild(buildText(items[c[0].i], c[0].i, ui, true, false));
        return stack;
      }
      var big = c.reduce(function (m, x) { return x.h > m.h ? x : m; }, c[0]).i;
      var k0 = lenClass(items[c[0].i].body);
      var even = c.every(function (x) { return lenClass(items[x.i].body) === k0; });
      c.forEach(function (x) { stack.appendChild(buildText(items[x.i], x.i, ui, false, even || x.i === big)); });
      return stack;
    });
  }

  // Photos and text columns alternate: photo x1, column, photo x1, column,
  // photo x2, column, photo x1, column... then whatever is left.
  var PATTERN = [1, 1, 2, 1];
  function sequence(photoNodes, colNodes) {
    var seq = [];
    var p = 0;
    var c = 0;
    var turn = 0;
    while (p < photoNodes.length || c < colNodes.length) {
      var k = PATTERN[turn++ % PATTERN.length];
      for (var n = 0; n < k && p < photoNodes.length; n++) seq.push(photoNodes[p++]);
      if (c < colNodes.length) seq.push(colNodes[c++]);
    }
    return seq;
  }

  function markDup(node) {
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('data-rm-dup', '');
    if (node.tagName === 'BUTTON') node.tabIndex = -1;
    [].slice.call(node.querySelectorAll('button')).forEach(function (b) { b.tabIndex = -1; });
  }

  // Lay nodes into a track. As a loop: repeat the set until one half covers
  // the lane, then duplicate the whole half; the speed setting (px/s) sets
  // the duration so the pace is the same however many reviews there are.
  function layTrack(track, nodes, loop, laneW, speed) {
    track.textContent = '';
    track.style.animationDuration = '';
    nodes.forEach(function (n) { track.appendChild(n); });
    if (!loop || !nodes.length) return;
    var guard = 0;
    while (track.offsetWidth < laneW && guard < 40) {
      nodes.forEach(function (n) { var c = n.cloneNode(true); markDup(c); track.appendChild(c); });
      guard++;
    }
    [].slice.call(track.children).forEach(function (n) {
      var c = n.cloneNode(true);
      markDup(c);
      track.appendChild(c);
    });
    var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    var dist = (track.offsetWidth + gap) / 2;
    track.style.animationDuration = Math.max(10, dist / (speed || 20)) + 's';
  }

  var mqDesktop = window.matchMedia('(min-width: 1000px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function render(root) {
    var st = root._kkRm;
    var items = st.items;
    var wrap = root.querySelector('[data-marquee-wrap]');
    var mainTrack = root.querySelector('[data-rm-main-track]');
    if (!wrap || !mainTrack) return;

    var photoIdx = [];
    var textIdx = [];
    var bubbleIdx = [];
    items.forEach(function (it, i) {
      var k = kindOf(it);
      if (k === 'photo') photoIdx.push(i);
      else if (k === 'text') textIdx.push(i);
      else bubbleIdx.push(i);
    });

    var cols = pack(wrap, items, textIdx, st.ui);
    var photos = photoIdx.map(function (i) { return buildPhoto(items[i], i, st.ui); });
    var seq = sequence(photos, cols);
    var bubbles = st.ui.showBubbles ? bubbleIdx.map(function (i) { return buildBubble(items[i]); }) : [];

    if (!seq.length && !bubbles.length) {
      root.setAttribute('data-state', 'empty');
      return;
    }

    var laneW = wrap.offsetWidth || window.innerWidth || 1280;
    var marquee = mqDesktop.matches && !mqReduce.matches;
    root.setAttribute('data-rm-marquee', marquee ? 'on' : 'off');
    var mainLane = root.querySelector('[data-rm-main]');
    if (mainLane) mainLane.scrollLeft = 0;
    root.querySelector('.kk-rm__main').hidden = !seq.length;
    layTrack(mainTrack, seq, marquee, laneW, st.ui.mainSpeed);

    var bubWrap = root.querySelector('[data-rm-bubbles]');
    var bubTrack = root.querySelector('[data-rm-bubbles-track]');
    if (bubWrap && bubTrack) {
      bubWrap.hidden = !bubbles.length;
      layTrack(bubTrack, bubbles, !mqReduce.matches, laneW, st.ui.bubblesSpeed);
    }
    root.setAttribute('data-state', 'ready');
  }

  // ---- Review modal -------------------------------------------------------
  function setupModal(root) {
    var modal = root.querySelector('[data-rm-modal]');
    var wrap = root.querySelector('[data-marquee-wrap]');
    if (!modal || !wrap) return;
    var media = modal.querySelector('[data-rm-m-media]');
    var hearts = modal.querySelector('[data-rm-m-hearts]');
    var quote = modal.querySelector('[data-rm-m-q]');
    var name = modal.querySelector('[data-rm-m-name]');
    var ok = modal.querySelector('[data-rm-m-ok]');
    var prod = modal.querySelector('[data-rm-m-prod]');
    var closeBtn = modal.querySelector('[data-rm-close]');
    var opener = null;
    var prevOverflow = '';

    function focusables() {
      return [].slice.call(modal.querySelectorAll('button, a[href], video[controls]')).filter(function (n) { return !n.closest('[hidden]'); });
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open(item, trigger) {
      opener = trigger;
      media.textContent = '';
      if (item.video.length) {
        var v = el('video');
        v.controls = true;
        v.autoplay = true;
        v.playsInline = true;
        v.preload = 'auto';
        if (item.img) v.poster = item.img;
        item.video.forEach(function (src) {
          var s = el('source');
          s.src = src.url;
          if (src.type) s.type = src.type;
          v.appendChild(s);
        });
        media.appendChild(v);
        var played = v.play && v.play();
        if (played && played.catch) played.catch(function () {});
      } else if (item.imgFull) {
        var img = el('img', 'kk-img-skel');
        img.src = item.imgFull;
        img.alt = fill(COPY.photoAlt, item);
        media.appendChild(img);
      }
      media.hidden = !media.firstChild;
      hearts.textContent = '';
      hearts.appendChild(heartsEl(item.rating));
      quote.textContent = item.body;
      quote.hidden = !item.body;
      name.textContent = item.name;
      ok.hidden = !item.verified;
      prod.textContent = item.product;
      if (item.url) prod.setAttribute('href', item.url); else prod.removeAttribute('href');
      prod.hidden = !item.product;
      modal.hidden = false;
      prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      closeBtn.focus();
    }

    function close() {
      if (modal.hidden) return;
      modal.hidden = true;
      media.textContent = ''; // stops any video
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    }

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('[data-rm-i]');
      if (!card || !root._kkRm) return;
      var item = root._kkRm.items[Number(card.getAttribute('data-rm-i'))];
      if (item) open(item, card);
    });
    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
  }

  function readManual(root) {
    var node = root.querySelector('[data-rm-manual]');
    if (!node) return [];
    try {
      var list = JSON.parse(node.textContent || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) { warn('manual reviews JSON unreadable', e); return []; }
  }

  function shuffle(arr) {
    var out = arr.slice();
    for (var i = out.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = out[i]; out[i] = out[j]; out[j] = tmp;
    }
    return out;
  }

  function filterAndSort(reviews, minRating) {
    return reviews.filter(function (r) {
      if (!r || r.hidden === true || r.published === false) return false;
      return (Number(r.rating) || 0) >= minRating;
    });
  }

  // Build once fonts are in (card heights depend on Bungee/Poppins metrics),
  // then re-pack whenever the width changes — card size steps at 640/1000px
  // and the base card width is viewport-relative.
  function start(root, apiReviews) {
    root._kkRm.items = readManual(root).map(fromManual).concat(shuffle(apiReviews).map(fromApi));
    var go = function () { render(root); };
    // fonts.ready alone isn't enough: Bungee is often first used by the
    // packer's own probe, so it hasn't even been requested yet. Load the
    // faces the cards use explicitly, then measure.
    var fs = document.fonts;
    if (fs && fs.load) {
      var cs = getComputedStyle(root);
      var display = cs.getPropertyValue('--kk-display').trim() || 'Bungee';
      var body = cs.getPropertyValue('--kk-body').trim() || 'Poppins';
      Promise.all([
        fs.load('400 22px ' + display),
        fs.load('500 14px ' + body),
        fs.load('600 16px ' + body),
        fs.load('700 13px ' + body)
      ]).then(go, go);
    } else {
      go();
    }
    if (root._kkRm.bound) return;
    root._kkRm.bound = true;
    var lastW = window.innerWidth;
    var t = null;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      clearTimeout(t);
      t = setTimeout(go, 150);
    });
    if (mqReduce.addEventListener) mqReduce.addEventListener('change', go);
  }

  function cacheKey(shop, perPage, minRating, collectionHandle) { return 'kkrm2:' + shop + ':' + perPage + ':' + minRating + ':' + (collectionHandle || ''); }
  function readCache(key) {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(key) || 'null');
      if (!parsed) return null;
      if (Date.now() - parsed.ts > CACHE_TTL_MS) { log('cache expired'); return null; }
      log('cache hit', key, '(' + parsed.reviews.length + ')');
      return parsed.reviews;
    } catch (e) { return null; }
  }
  function writeCache(key, reviews) {
    try { window.localStorage.setItem(key, JSON.stringify({ ts: Date.now(), reviews: reviews })); }
    catch (e) { warn('cache write failed', e); }
  }

  function fetchReviews(cfg) {
    var url = cfg.proxyUrl + '?per_page=' + encodeURIComponent(cfg.perPage);
    if (cfg.collectionHandle) url += '&collection_handle=' + encodeURIComponent(cfg.collectionHandle);
    log('fetching', url);
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (res) {
      log('response status', res.status);
      if (!res.ok) throw new Error('Proxy HTTP ' + res.status);
      return res.json();
    });
  }

  function fetchStats(cfg) {
    var url = cfg.proxyUrl + '?stats=1';
    if (cfg.collectionHandle) url += '&collection_handle=' + encodeURIComponent(cfg.collectionHandle);
    log('fetching stats', url);
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('Stats HTTP ' + res.status);
      return res.json();
    });
  }

  function formatCount(n) {
    var v = Math.max(0, Math.floor(Number(n) || 0));
    return v.toLocaleString('en-US');
  }

  function applyStats(root, stats) {
    if (!stats || typeof stats.count !== 'number') return;
    var avgEl = root.querySelector('[data-rm-avg]');
    var scoreEl = root.querySelector('[data-rm-score]');
    var countEl = root.querySelector('[data-rm-count]');
    var sepEl = root.querySelector('[data-rm-sep]');
    var heartsEl = root.querySelector('[data-rm-hearts]');
    var avgText = stats.average > 0 ? stats.average.toFixed(1) : '';
    var countText = formatCount(stats.count);

    if (heartsEl && stats.average > 0) {
      // Server pre-rendered 5 hearts (or however many the section setting
      // resolved to) — swap them for the live average so the visible heart
      // count tracks the number next to it.
      heartsEl.innerHTML = '<span class="kk-rm__stats-stars" style="display:inline-flex;align-items:center;gap:2px;line-height:1;" aria-hidden="true">' + heartsHtml(stats.average, 18) + '</span>';
    }

    // The "/5.0" suffix lives beside the number inside [data-rm-score]; hide
    // the pair together so a stats failure never leaves a bare "/5.0".
    if (avgEl && avgText) avgEl.textContent = avgText;
    if (scoreEl) scoreEl.hidden = !avgText;
    if (countEl) {
      if (stats.count > 0) {
        var tpl = countEl.innerHTML;
        // Prefer explicit {count} placeholder; otherwise replace the first
        // digit-group in the template (e.g. legacy "1,247") so existing
        // settings keep working without a migration.
        if (tpl.indexOf('{count}') !== -1) {
          countEl.innerHTML = tpl.replace(/\{count\}/g, countText);
        } else {
          countEl.innerHTML = tpl.replace(/[\d,]+/, countText);
        }
        countEl.hidden = false;
      } else {
        countEl.hidden = true;
      }
    }
    if (sepEl) sepEl.hidden = !(avgText && stats.count > 0);
  }

function init(root) {
    if (root.dataset.kkBound) return;
    root.dataset.kkBound = '1';
    var ds = root.dataset;
    COPY.reviewerFallback = (ds.reviewerFallback || '').trim() || COPY.reviewerFallback;
    COPY.verifiedLabel = (ds.verifiedLabel || '').trim() || COPY.verifiedLabel;
    COPY.readLabel = (ds.readLabel || '').trim() || COPY.readLabel;
    COPY.watchLabel = (ds.watchLabel || '').trim() || COPY.watchLabel;
    COPY.byline = (ds.byline || '').trim() || COPY.byline;
    COPY.cardAria = (ds.cardAria || '').trim() || COPY.cardAria;
    COPY.photoAlt = (ds.photoAlt || '').trim() || COPY.photoAlt;
    root._kkRm = {
      items: [],
      ui: {
        showVerified: ds.showVerified !== 'false',
        showCta: ds.showCta !== 'false',
        showBubbles: ds.showBubbles !== 'false',
        mainSpeed: parseFloat(ds.mainSpeed) || 26,
        bubblesSpeed: parseFloat(ds.bubblesSpeed) || 18
      }
    };
    var cfg = {
      proxyUrl: (function () {
        var raw = (root.getAttribute('data-proxy-url') || '').trim().replace(/\/$/, '');
        if (!raw) return '';
        return /^https?:\/\//i.test(raw) ? raw : 'https://' + raw;
      })(),
      perPage: parseInt(root.getAttribute('data-per-page'), 10) || 40,
      minRating: parseInt(root.getAttribute('data-min-rating'), 10) || 4,
      collectionHandle: (root.getAttribute('data-collection-handle') || '').trim() || null
    };
    log('init', cfg);
    setupModal(root);

    if (!cfg.proxyUrl) {
      warn('no proxy URL configured — manual reviews only');
      start(root, []);
      return;
    }

    var key = cacheKey(cfg.proxyUrl, cfg.perPage, cfg.minRating, cfg.collectionHandle);
    var cached = readCache(key);
    var apply = function (raw) {
      var reviews = filterAndSort(raw, cfg.minRating);
      log('usable reviews', reviews.length, 'of', raw.length);
      start(root, reviews);
    };

    // Stats are independent of the card render — fire and forget so a stats
    // failure never blocks the cards (and vice versa).
    fetchStats(cfg)
      .then(function (s) { applyStats(root, s); })
      .catch(function (e) { warn('stats fetch failed — using section defaults', e); });

    if (cached) { apply(cached); return; }

    fetchReviews(cfg)
      .then(function (data) {
        var raw = (data && data.reviews) || [];
        log('fetched reviews', raw.length);
        writeCache(key, raw);
        apply(raw);
      })
      .catch(function (e) {
        err('fetch failed — falling back to manual reviews only', e);
        start(root, []);
      });
  }

  function boot() {
    var roots = document.querySelectorAll('.kk-rm');
    log('found', roots.length, 'marquee section(s)');
    [].slice.call(roots).forEach(init);
  }

  // Theme editor: a re-rendered section arrives as a fresh root.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector && e.target.querySelector('.kk-rm');
    if (root) init(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
